import Plugin from '@ckeditor/ckeditor5-core/src/plugin';
import imageIcon from './keyword_icon.svg';
import Command from '@ckeditor/ckeditor5-core/src/command';
import {toWidget, viewToModelPositionOutsideModelElement} from '@ckeditor/ckeditor5-widget/src/utils';
import Widget from '@ckeditor/ckeditor5-widget/src/widget';

import {addListToDropdown, createDropdown} from '@ckeditor/ckeditor5-ui/src/dropdown/utils';
import Collection from '@ckeditor/ckeditor5-utils/src/collection';
import Model from '@ckeditor/ckeditor5-ui/src/model';
import { ContextualBalloon, clickOutsideHandler } from 'ckeditor5/src/ui';
import {getBalloonPositionData} from "@ckeditor/ckeditor5-image/src/image/ui/utils";
import {KeywordView} from "./keywordView";

class SBKeywordCommand extends Command {
	execute( {value} ) {
		const editor = this.editor;
		const selection = editor.model.document.selection;

		editor.model.change( writer => {

			const placeholder = writer.createElement( 'placeholder', {
				...Object.fromEntries( selection.getAttributes() ),
				name: value.variable
			} );

			// ... and insert it into the document.
			editor.model.insertContent( placeholder );

			// Put the selection on the inserted element.
			// writer.setSelection( placeholder, 'on' );
		} );
	}

	refresh() {
		const model = this.editor.model;
		const selection = model.document.selection;

		const isAllowed = model.schema.checkChild( selection.focus.parent, 'placeholder' );
		this.isEnabled = isAllowed;
	}
}

export class InsertSBKeywordPlugin extends Plugin {
	static get requires() {
		return [ Widget, ContextualBalloon ];
	}

	static get pluginName() {
		return 'InsertSBKeyword';
	}

	init() {
		this._defineSchema();
		this._defineConverters();

		const editor = this.editor;
		const view = editor.editing.view;
		const viewDocument = view.document;
		const projectTokens = Array.isArray(editor.config.get( 'sbConfig.projectTokens' ))
							 ? editor.config.get( 'sbConfig.projectTokens' ) : [];

		this._balloon = editor.plugins.get( ContextualBalloon );
		this._keywordView = new KeywordView();

		this.editor.commands.add( 'placeholder', new SBKeywordCommand( this.editor ) );
		this.editor.editing.mapper.on(
			'viewToModelPosition',
			viewToModelPositionOutsideModelElement( this.editor.model, viewElement => viewElement.hasClass( 'placeholder' ) )
		);

		editor.ui.componentFactory.add( 'insertSBKeyword', locale => {
			const view = new createDropdown( locale );
			const itemDefinitions = new Collection();
			const command = editor.commands.get( 'placeholder' );

			for(const token of projectTokens) {
				let answer = this._truncateTokenAnswer(token.answer);
				const def = {
					type: 'button',
					model: new Model( {
						smartBuilderToken: token,
						label: `${token.variable} - ${answer}`,
						withText: true
					} )
				};

				// TODO: This isn't working for some reason
				def.model.bind( 'isOn' ).to( command, 'value', value => {
					return value === token.variable;
				});
				itemDefinitions.add( def );
			}

			addListToDropdown(view, itemDefinitions);
			view.buttonView.set( {
				label: 'Insert Keyword',
				icon: imageIcon,
				tooltip: true
			});

			this.listenTo(view, 'execute', evt => {
				editor.execute( 'placeholder', { value: evt.source.smartBuilderToken } );
				editor.editing.view.focus();
			});

			return view;
		});

		this.listenTo( editor.ui, 'update', () => {
			const viewElement = viewDocument.selection.getSelectedElement();
			if(viewElement && viewElement.getCustomProperty('sb-placeholder')) {
				if(!this._balloon.hasView(this._keywordView)) {
					this._balloon.add({
						view: this._keywordView,
						position: getBalloonPositionData(editor)
					});
				}else{
					const position = getBalloonPositionData( editor );
					this._balloon.updatePosition( position );
				}

				const token = viewElement.getCustomProperty('sb-placeholder');
				let projectToken = projectTokens.find(f => f.variable === token);
				if(projectToken) {
					projectToken = this._truncateTokenAnswer(projectToken.answer);
				}else{
					projectToken = 'UNDEFINED';
				}

				this._keywordView.value = projectToken;
			}
		});


		clickOutsideHandler( {
			emitter: this._keywordView,
			activator: () => this._balloon.hasView(this._keywordView),
			contextElements: [ this._balloon.view.element ],
			callback: () => this._closeUI()
		} );
	}

	_truncateTokenAnswer(answer) {
		answer = answer ? answer : '';
		if(answer.length > 50) {
			answer = answer.substr(0, 50) + '...';
		}

		return answer;
	}

	_closeUI() {
		if(this._balloon.hasView(this._keywordView)) {
			this._balloon.remove( this._keywordView );
		}
	}

	_defineSchema() {
		const schema = this.editor.model.schema;

		schema.register('placeholder', {
			// Allow wherever text is allowed:
			allowWhere: '$text',

			// The placeholder will act as an inline node:
			isInline: true,

			// The inline widget is self-contained so it cannot be split by the caret and it can be selected:
			isObject: true,

			// The inline widget can have the same attributes as text (for example linkHref, bold).
			allowAttributesOf: '$text',

			// The placeholder can have many types, like date, name, surname, etc:
			allowAttributes: ['name']
		});
	}

	_defineConverters() {
		const conversion = this.editor.conversion;

		conversion.for('upcast').elementToElement({
			view: {
				name: 'span',
				classes: ['placeholder']
			},
			model: (viewElement, {writer: modelWriter}) => {
				let name = '';
				if(viewElement.getChild(0)) {
					name = viewElement.getChild(0).data;
				}
				return modelWriter.createElement('placeholder', {name});
			}
		});

		conversion.for('editingDowncast').elementToElement({
			model: 'placeholder',
			view: (modelItem, {writer: viewWriter}) => {
				const name = modelItem.getAttribute('name');
				const placeholderView = viewWriter.createContainerElement('span', {
					class: 'placeholder'
				}, {
					isAllowedInsideAttributeElement: true
				});

				const innerText = viewWriter.createText(name);
				viewWriter.insert(viewWriter.createPositionAt(placeholderView, 0), innerText);
				viewWriter.setCustomProperty( 'sb-placeholder', name, placeholderView );

				// Enable widget handling on a placeholder element inside the editing view.
				return toWidget(placeholderView, viewWriter);
			}
		});

		conversion.for('dataDowncast').elementToElement({
			model: 'placeholder',
			view: (modelItem, {writer: viewWriter}) => {
				const name = modelItem.getAttribute('name');

				const placeholderView = viewWriter.createContainerElement('span', {
					class: 'placeholder'
				}, {
					isAllowedInsideAttributeElement: true
				});

				// Insert the placeholder name (as a text).
				const innerText = viewWriter.createText(name);
				viewWriter.insert(viewWriter.createPositionAt(placeholderView, 0), innerText);

				return placeholderView;
			}
		});
	}
}
